// frontend/src/pages/Help.js
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  NewReleases as NewReleasesIcon,
  Article as ArticleIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useThemeSettings } from '../contexts/ThemeContext';

const Help = () => {
  const { t } = useThemeSettings();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState('v260127a');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const releaseNotes = [
    {
      version: 'v260127a',
      title: 'Tickets Overhaul & Keyboard Shortcuts',
      releaseNotesVersion: 'v260127a',
      date: '27/01/2026',
      summary: 'Volledig vernieuwd ticketbeheer met toetsenbord snelkoppelingen, verbeterde bewerking en server-side zoeken.',
      highlights: [
        '⌨️ Keyboard Shortcuts - C=nieuw, /=zoeken, F=filter, R=refresh, ↑↓=navigeren, Esc=terug',
        '✏️ Inline Bewerking - Locatie, apparaat en volgende stap direct aanpassen',
        '📋 Next Step Kolom - Zie direct de volgende actie in de ticketlijst',
        '🔍 Server-side Zoeken - Doorzoek alle tickets, niet alleen de laatste 100',
        '🔗 Klikbare Links - Navigeer direct naar locatie of apparaat'
      ]
    },
    {
      version: 'v251217x',
      title: 'Site Filter & Toegangsbeheer',
      releaseNotesVersion: 'v251217x',
      date: '17/12/2025',
      summary: 'Nieuwe Site Switcher voor snel wisselen tussen locaties en granulaire toegangscontrole per site.',
      highlights: [
        '🏢 Site Switcher - Snel wisselen tussen locaties in de navigatiebalk',
        '🔒 Site Toegangsbeheer - Wijs specifieke sites toe aan gebruikers',
        '🔍 Automatische filtering op alle pagina\'s (locaties, apparaten, tickets, alarmen)',
        '🛠️ Bug fixes: Field Interventions loop, device pagination, location filter timing'
      ]
    },
    {
      version: 'v251107a',
      title: 'UX overhaul',
      releaseNotesVersion: 'v20251107a',
      date: '07/11/2025',
      summary: 'Volledige herwerking van de gebruikerservaring met verbeterde navigatie, thema-opties en toegankelijkheidsfuncties.',
      highlights: [
        //snellere laadtijden, skeleton loaders, volledige vertaling
        '⚡ Snellere laadtijden met geoptimaliseerde code en assets',
        '🧩 Nieuwe Skeleton Loaders voor vloeiendere contentweergave'
        ],
    },
    {
      version: 'v2510028a',
      title: 'Camera Requests & Improvements',
      releaseNotesVersion: 'v2510028a',
      date: '28/10/2025',
      summary: 'Camera beelden aanvragen en algemene verbeteringen aan het systeem.',
      highlights: [
        '🛠️ Bug fixes and performance improvements',
        '📸 Camera Footage Requests - Vraag eenvoudig camerabeelden aan via CARO',
        '📝 Interventieverslagen bij fysieke interventies',
        '🔍 Verbeterde ticket zoekfunctie om locaties te vinden'
      ]
    },
    {
      version: 'v251007a',
      title: 'Enhanced Search & Animations',
      date: '07/10/2025',
      releaseNotesVersion: 'v251007a',
      summary: 'Verbeterde zoekfunctionaliteit met fuzzy search en dynamische animaties voor een soepelere gebruikerservaring.',
      highlights: [
        '🔍 Fuzzy Search - Vind sneller wat je zoekt',
        '🎨 Dynamische Animaties - Verbeterde gebruikerservaring',
        '⚡ Snellere laadtijden en responsieve interface'
      ]
    },
    {
      version: 'v251006c',
      title: 'Multi-Language Support',
      date: '06/10/2025',
      releaseNotesVersion: 'v251006c',
      summary: 'CARO spreekt nu jouw taal! Volledige meertalige ondersteuning toegevoegd met real-time taal switching.',
      highlights: [
        '🌍 Multi-Language Support - CARO spreekt nu jouw taal!',
        '🇳🇱 Nederlands, 🇬🇧 English, 🇫🇷 Français, 🇩🇪 Deutsch',
        '🟡 West-Vlaams en 💎 Antwerps dialecten toegevoegd',
        '⚡ Real-time taal switching zonder pagina herlaad',
        '📅 Localized datum en tijd formaten per regio',
        '🔧 Ga naar Instellingen > Taal & Regio om te wijzigen'
      ]
    },
    {
      version: 'v251006b',
      title: 'Complete Mobile & Tablet Overhaul',
      date: '06/10/2025',
      releaseNotesVersion: 'v251006b',
      summary: 'Volledige herwerking van de mobiele en tablet ervaring met responsive design en touch-optimalisatie.',
      highlights: [
        '📱 Complete Mobile & Tablet Overhaul - Geen horizontaal scrollen meer!',
        '⚡ Quick Actions Dashboard voor snelle toegang tot functies',
        '📋 Tabellen → Kaarten op mobiel voor betere leesbaarheid',
        '👆 Touch-geoptimaliseerde knoppen en interface',
        '📐 Volledige schermweergave voor pop-ups op mobiel',
        '🎯 Menu\'s passen zich automatisch aan (tabs → accordions)',
        '✅ Getest op alle apparaten: iPhone, Android, iPad, tablets'
      ]
    },
    {
      version: 'v2.1.0 (v251003)',
      title: 'Initial Release - Complete Portal System',
      date: '03/10/2025',
      releaseNotesVersion: 'v251003',
      summary: 'Eerste versie van het CARO Management Portal met volledige ticketing, locatie- en apparaatbeheer.',
      highlights: [
        '🎯 Live ticketing systeem - Meld problemen en volg de status',
        '📊 Real-time dashboard met statistieken',
        '🏢 Locatiebeheer met hiërarchische structuur',
        '💻 Apparaatbeheer met online/offline status',
        '📱 Progressive Web App (PWA) - Installeer als desktop/mobiele app',
        '🔐 Microsoft SSO met Multi-Factor Authenticatie',
        '⚙️ Uitgebreide instellingen - Thema\'s, lettertypen en toegankelijkheid',
        '👥 Gebruikersbeheer met rol- en permissie systeem',
        '🌐 Multi-tenant architectuur per bedrijf',
        '📧 E-mail notificaties voor ticket updates',
        '💬 Commentaar en communicatie binnen tickets'
      ]
    }
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }} className="page-transition-enter">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" className="slide-down" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NewReleasesIcon fontSize="large" color="primary" />
          {t ? t('Help.help_center') : 'Help Center'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t ? t('Help.welcome_message') : 'Welcome to the CARO Portal Help Center. Find release notes, updates, and documentation.'}
        </Typography>
      </Box>

      {/* Release Notes Section */}
      <Paper className="card-animated card-enter" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          {t ? t('Help.release_notes') : 'Release Notes'}
        </Typography>

        {releaseNotes.map((release) => (
          <Accordion
            key={release.version}
            className="list-item-enter"
            expanded={expanded === release.version}
            onChange={handleChange(release.version)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${release.version}-content`}
              id={`${release.version}-header`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Chip
                  label={release.version}
                  color="primary"
                  size="small"
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {release.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t ? t('Help.released_on') : 'Released on'} {release.date}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {release.summary}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  {t ? t('Help.highlights') : 'Key Highlights'}:
                </Typography>
                <List dense>
                  {release.highlights.map((highlight, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={highlight}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArticleIcon />}
                    onClick={() => navigate(`/release-notes/${release.releaseNotesVersion}`)}
                  >
                    {t ? t('Help.view_full_release_notes') : 'View Full Release Notes'}
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Coming Soon Section */}
      <Paper className="card-animated card-enter" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t ? t('Help.coming_soon') : 'Coming Soon'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t ? t('Help.coming_soon_message') : 'User guides, tutorials, and more documentation will be added here soon.'}
        </Typography>
      </Paper>
    </Box>
  );
};

export default Help;
